import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";

const LOGO =
  "https://customer-assets.emergentagent.com/job_andrews-pink-co/artifacts/c2dqbyuo_icm_fullxfull.893442695_cpvpdh1kpncc4cokk0ws.jpg";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/policies", label: "Policies" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openCart, totalQuantity } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`glass-nav sticky top-0 z-40 w-full transition-all duration-500 ${
        scrolled ? "py-1 shadow-soft" : "py-3"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="group flex items-center gap-3 sm:gap-4"
          data-testid="logo-link"
          aria-label="Uniquely Pink home"
        >
          <span
            className={`relative block overflow-hidden rounded-full ring-2 ring-hotpink/40 transition-all duration-500 group-hover:ring-hotpink group-hover:rotate-[-6deg] ${
              scrolled
                ? "h-14 w-14 sm:h-16 sm:w-16"
                : "h-16 w-16 sm:h-20 sm:w-20 lg:h-24 lg:w-24"
            }`}
          >
            <img
              src={LOGO}
              alt="Uniquely Pink logo, pink bow and sparkles"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
              loading="eager"
            />
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-hotpink/0 via-white/30 to-hotpink/0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
          </span>
          <span
            className={`font-script leading-none text-hotpink transition-all duration-500 hidden sm:block ${
              scrolled ? "text-3xl" : "text-4xl lg:text-5xl"
            }`}
          >
            Uniquely Pink
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `group relative text-sm font-medium tracking-wide transition-colors ${
                  isActive
                    ? "text-magenta"
                    : "text-plum/80 hover:text-hotpink"
                }`
              }
              end={l.to === "/"}
            >
              {({ isActive }) => (
                <>
                  <span className="transition-transform duration-300 group-hover:-translate-y-0.5 inline-block">
                    {l.label}
                  </span>
                  <span
                    className={`absolute -bottom-1.5 left-1/2 h-[3px] -translate-x-1/2 rounded-full bg-hotpink transition-all duration-300 ${
                      isActive
                        ? "w-5"
                        : "w-0 group-hover:w-4"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openCart}
            data-testid="cart-trigger"
            aria-label="Open shopping cart"
            className="relative grid h-11 w-11 place-items-center rounded-full bg-white/80 text-hotpink shadow-soft transition-all duration-300 hover:bg-white hover:shadow-glow hover:-translate-y-0.5 active:scale-95"
          >
            <ShoppingBag className="h-5 w-5" />
            {totalQuantity > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-hotpink text-[10px] font-bold text-white animate-bounce-once"
              >
                {totalQuantity}
              </span>
            )}
          </button>

          <button
            type="button"
            className="grid h-11 w-11 place-items-center rounded-full bg-white/80 text-plum shadow-soft transition-all duration-300 hover:bg-white active:scale-95 md:hidden"
            onClick={() => setMobileOpen((s) => !s)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="animate-fadeUp border-t border-hotpink/10 bg-white/95 px-4 py-4 md:hidden"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col gap-2" aria-label="Mobile primary">
            {links.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                style={{ animationDelay: `${i * 60}ms` }}
                className={({ isActive }) =>
                  `animate-fadeUp rounded-full px-4 py-2 text-base font-medium transition-all ${
                    isActive
                      ? "bg-hotpink text-white shadow-soft"
                      : "text-plum hover:bg-blush"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
